import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { ImageOff } from "lucide-react";

// Üretim Kanban kart sunum parçaları (salt-okunur; iş kuralı içermez)
const thumbCache = new Map(); // `${pid}:${imgId}` → Promise<objectURL|null>

const loadThumb = (pid, imgId) => {
  const k = `${pid}:${imgId}`;
  if (!thumbCache.has(k)) {
    thumbCache.set(k, api.get(`/products/${pid}/images/${imgId}/thumb`, { responseType: "blob" })
      .then((r) => URL.createObjectURL(r.data)).catch(() => null));
  }
  return thumbCache.get(k);
};

export const ProductThumb = ({ productId, imageId, testId }) => {
  const ref = useRef(null);
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!productId || !imageId || !ref.current) return undefined;
    let alive = true;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      loadThumb(productId, imageId).then((u) => alive && setUrl(u));
    }, { rootMargin: "200px" });
    io.observe(ref.current);
    return () => { alive = false; io.disconnect(); };
  }, [productId, imageId]);
  return (
    <div ref={ref} data-testid={testId}
      className="w-16 h-16 shrink-0 rounded-sm border border-slate-800 bg-slate-900 flex items-center justify-center overflow-hidden">
      {url ? <img src={url} alt="" className="w-full h-full object-cover" data-testid={`${testId}-img`} />
        : !imageId && (
          <div className="flex flex-col items-center gap-0.5 text-slate-600" data-testid={`${testId}-empty`}>
            <ImageOff className="w-4 h-4" />
            <span className="text-[8px] uppercase tracking-widest font-mono">Görsel yok</span>
          </div>
        )}
    </div>
  );
};

const DAY = 86400000;
export const PlanRemaining = ({ due, done, testId }) => {
  const d = due ? new Date(due) : null;
  const valid = d && !Number.isNaN(d.getTime());
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = valid ? Math.round((new Date(d).setHours(0, 0, 0, 0) - today) / DAY) : null;
  const rem = done ? { t: "Tamamlandı", c: "text-emerald-300 border-emerald-900 bg-emerald-950/40" }
    : days === null ? { t: "—", c: "text-slate-500 border-slate-800" }
    : days < 0 ? { t: `${-days} gün gecikti`, c: "text-red-300 border-red-900 bg-red-950/50" }
    : days === 0 ? { t: "Bugün", c: "text-amber-300 border-amber-900 bg-amber-950/40" }
    : { t: `${days} gün`, c: days <= 3 ? "text-amber-300 border-amber-900/70" : "text-slate-200 border-slate-800" };
  return (
    <div className="grid grid-cols-2 gap-1.5 text-[10px] font-mono" data-testid={testId}>
      <div className="border border-slate-800 rounded-sm px-1.5 py-1 text-slate-400 truncate" data-testid={`${testId}-plan`}>
        Plan: <span className="text-slate-200">{valid ? d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "2-digit" }) : "—"}</span>
      </div>
      <div className={`border rounded-sm px-1.5 py-1 truncate ${rem.c}`} data-testid={`${testId}-remaining`} data-overdue={days !== null && days < 0 && !done ? "1" : "0"}>
        {done || (days !== null && days < 0) ? <span className="font-semibold">{rem.t}</span> : <>Kalan: <span className="font-semibold">{rem.t}</span></>}
      </div>
    </div>
  );
};

const SIZE_ORDER = ["XXS", "XS", "S", "M", "L", "XL", "XXL", "2XL", "XXXL", "3XL", "4XL", "5XL", "6XL"];
const sizeRank = (s) => {
  const i = SIZE_ORDER.indexOf(String(s).toUpperCase());
  if (i >= 0) return i;
  const n = parseFloat(s);
  return Number.isFinite(n) ? 100 + n : 1000;
};

// [[etiket, adet], ...] — boş etiketli satır varsa dağılım güvenilir değil → null ("—")
export const buildDistribution = (pairs) => {
  if (!pairs.length || pairs.some(([l]) => !l)) return null;
  const m = new Map();
  pairs.forEach(([l, q]) => m.set(l, (m.get(l) || 0) + (Number(q) || 0)));
  return [...m.entries()].sort((a, b) => sizeRank(a[0]) - sizeRank(b[0]));
};

export const VariantChips = ({ dist, testId }) => (
  <div className="flex flex-wrap gap-1" data-testid={testId}>
    {dist ? dist.map(([l, q]) => (
      <span key={l} className="inline-flex items-baseline gap-1 text-[10px] font-mono border border-slate-700 bg-slate-900 rounded-sm px-1.5 py-0.5"
        data-testid={`${testId}-chip`}>
        <span className="text-slate-300 font-semibold">{l}</span><span className="text-white tabular-nums">{q}</span>
      </span>
    )) : <span className="text-[10px] font-mono text-slate-600">Beden/varyant: —</span>}
  </div>
);

export const StageProgress = ({ label, done, total, testId }) => {
  const pct = Math.min(100, Math.round((done / total) * 100));
  return (
    <div data-testid={testId}>
      <div className="flex justify-between text-[10px] font-mono mb-0.5">
        <span className="text-slate-400">{label}</span>
        <span className="text-slate-200 tabular-nums" data-testid={`${testId}-value`}>{done}/{total} (%{pct})</span>
      </div>
      <div className="h-1 bg-slate-800 rounded-full overflow-hidden">
        <div className={`h-full ${pct >= 100 ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};
